import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Progress, Select, Textarea } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useTimeZone } from '@/providers/TimeZoneProvider'
import { formatDateTimeLong } from '@/utils/timezone'
import { toast } from 'react-hot-toast'
import { useShiftsByDispatcher } from '@/hooks/api/useShifts'
import { useShiftDefectAction } from '@/hooks/api/useShiftDefects'
import { uploadService } from '@/api/services/uploadService'
import { inputStyle } from '@/utils/userUtils'

const REASON_OPTIONS = [
  { label: 'Medical', value: 'medical' },
  { label: 'Emergency', value: 'emergency' },
  { label: 'Network / Technical', value: 'network/technical' },
  { label: 'Scheduling Conflict', value: 'scheduling conflict' },
  { label: 'Others', value: 'others' },
]

const MAX_FILE_SIZE_MB = 5
const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png']

interface ShiftDefectExplanationModalProps {
  open: boolean
  dispatcherName: string
  dispatcherId: string
  shiftId: string
  defectType: string
  onSubmitted: () => void
}

export const ShiftDefectExplanationModal = ({
  open,
  dispatcherName,
  dispatcherId,
  shiftId,
  defectType,
  onSubmitted,
}: ShiftDefectExplanationModalProps) => {
  const { timeZone } = useTimeZone()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadStatus, setUploadStatus] = useState<
    'idle' | 'uploading' | 'success' | 'error'
  >('idle')
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null)
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)

  const { data: shiftsData, isLoading: isLoadingShift } = useShiftsByDispatcher(
    dispatcherId,
    {
      limit: 200,
      sortBy: 'from',
      sortOrder: 'desc',
    }
  )
  const { mutate: submitExplanation, isPending: isSubmitting } =
    useShiftDefectAction()

  const shiftDetails = useMemo(
    () => shiftsData?.items?.find(shift => shift.shift_id === shiftId),
    [shiftsData?.items, shiftId]
  )

  const form = useForm({
    initialValues: {
      reason: '',
      description: '',
    },
    validate: {
      reason: value => (!value ? 'Reason is required' : null),
      description: value => (!value ? 'Reason description is required' : null),
    },
  })

  useEffect(() => {
    if (!open) return
    form.reset()
    setUploadProgress(0)
    setUploadStatus('idle')
    setUploadedUrl(null)
    setUploadedFileName(null)
  }, [open])

  useEffect(() => {
    if (!open) return
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
      }
    }

    document.addEventListener('keydown', handleKeydown, true)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeydown, true)
      document.body.style.overflow = ''
    }
  }, [open])

  const formattedShift = useMemo(() => {
    if (!shiftDetails) {
      if (isLoadingShift) {
        return {
          shiftType: 'Loading...',
          from: 'Loading...',
          to: 'Loading...',
        }
      }
      return {
        shiftType: 'Shift not found',
        from: 'N/A',
        to: 'N/A',
      }
    }
    const shiftType = shiftDetails.shift_type
      ? `${shiftDetails.shift_type.charAt(0).toUpperCase()}${shiftDetails.shift_type.slice(
          1
        )} Shift`
      : 'Shift'
    const from = shiftDetails.from
      ? formatDateTimeLong(shiftDetails.from, timeZone, 'N/A')
      : 'N/A'
    const to = shiftDetails.to
      ? formatDateTimeLong(shiftDetails.to, timeZone, 'N/A')
      : 'N/A'
    return { shiftType, from, to }
  }, [shiftDetails, isLoadingShift])

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error('Unsupported file format. Please upload PDF, JPG, or PNG.')
      event.target.value = ''
      return
    }

    const maxBytes = MAX_FILE_SIZE_MB * 1024 * 1024
    if (file.size > maxBytes) {
      toast.error('File is too large. Max size is 5MB.')
      event.target.value = ''
      return
    }

    setUploadStatus('uploading')
    setUploadProgress(0)
    setUploadedUrl(null)
    setUploadedFileName(file.name)

    try {
      const imageURL = await uploadService.uploadImage(file, progress => {
        setUploadProgress(progress)
      })
      setUploadedUrl(imageURL)
      setUploadStatus('success')
      setUploadProgress(100)
      toast.success('Document uploaded successfully')
    } catch (error: any) {
      console.error('Failed to upload document:', error)
      setUploadStatus('error')
      setUploadProgress(0)
      toast.error(error?.message || 'Failed to upload document')
    }
  }

  const handleSubmit = (values: { reason: string; description: string }) => {
    submitExplanation(
      {
        shift_id: shiftId,
        dispatcher_id: dispatcherId,
        defect_type: defectType,
        action_type: 'dispatcher_response',
        payload: {
          reason: values.reason,
          explanation: values.description,
          supporting_document_urls: uploadedUrl ? [uploadedUrl] : [],
        },
      },
      {
        onSuccess: response => {
          toast.success(
            response.message || 'Explanation submitted successfully'
          )
          onSubmitted()
        },
        onError: (error: any) => {
          console.error('Failed to submit explanation:', error)
          toast.error(error?.message || 'Failed to submit explanation')
        },
      }
    )
  }

  if (!open) return null

  const isSubmitDisabled =
    !form.values.reason ||
    !form.values.description ||
    isSubmitting ||
    uploadStatus === 'uploading'

  return (
    <div
      className="fixed inset-0 z-9999 flex items-center justify-center bg-black/60 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="shift-defect-title"
      aria-describedby="shift-defect-description"
    >
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl p-6">
        <div className="pb-3">
          <div
            id="shift-defect-title"
            className="text-sm font-semibold text-[#64676A] border-b border-gray-200 pb-2"
          >
            Default Explanation
          </div>
          <p
            id="shift-defect-description"
            className="text-sm font-medium text-[#1C1B1B] pt-4"
          >
            You missed a required shift event. Please provide an explanation.
          </p>
        </div>

        <div className="text-sm text-gray-700 space-y-2 pb-4 border-b border-gray-200">
          <div>
            <span className="font-medium text-gray-600">Name:</span>{' '}
            <span className="font-semibold text-gray-900">
              {dispatcherName || 'Dispatcher'}
            </span>
          </div>
          <div>
            <span className="font-medium text-gray-600">Shift:</span>{' '}
            <span className="font-semibold text-gray-900">
              {isLoadingShift ? 'Loading...' : formattedShift.shiftType}
            </span>
          </div>
          <div className="flex items-start gap-2">
            <span className="font-medium text-gray-600">From:</span>
            <span className="text-gray-800">{formattedShift.from}</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="font-medium text-gray-600">To:</span>
            <span className="text-gray-800">{formattedShift.to}</span>
          </div>
        </div>

        <form onSubmit={form.onSubmit(handleSubmit)} className="space-y-4 pt-2">
          <Select
            label="Reason for Default"
            placeholder="Select reason"
            data={REASON_OPTIONS}
            required
            comboboxProps={{ withinPortal: false, zIndex: 10001 }}
            styles={inputStyle}
            {...form.getInputProps('reason')}
          />

          <Textarea
            label="Reason Description"
            placeholder="Provide a detailed explanation"
            required
            minRows={3}
            autosize
            styles={inputStyle}
            {...form.getInputProps('description')}
          />

          <div className="border border-dashed border-blue-400 rounded-lg p-4 text-center text-sm text-gray-600">
            <div className="font-medium text-gray-800 mb-1">
              Add any required documents here.
            </div>
            <div>PDF, JPG, PNG | Max size: 5MB</div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="hidden"
            />

            <div className="mt-3 flex flex-col items-center gap-2">
              <Button
                type="button"
                variant="outline"
                color="#254769"
                style={{ borderRadius: '6px' }}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadStatus === 'uploading'}
              >
                {uploadedFileName ? 'Replace file' : 'Choose file'}
              </Button>

              {uploadedFileName && (
                <div className="text-xs text-gray-500">{uploadedFileName}</div>
              )}

              {uploadStatus === 'uploading' && (
                <div className="w-full">
                  <Progress value={uploadProgress} size="sm" />
                  <div className="text-xs text-gray-500 mt-1">
                    Uploading {uploadProgress}%
                  </div>
                </div>
              )}

              {uploadStatus === 'success' && (
                <div className="text-xs text-green-600">Upload complete</div>
              )}

              {uploadStatus === 'error' && (
                <div className="text-xs text-red-500">
                  Upload failed. Please try again.
                </div>
              )}
            </div>
          </div>

          <Button
            type="submit"
            fullWidth
            loading={isSubmitting}
            disabled={isSubmitDisabled}
            variant="filled"
            color="#254769"
            style={{ borderRadius: '6px' }}
          >
            Submit Explanation
          </Button>
        </form>
      </div>
    </div>
  )
}

export default ShiftDefectExplanationModal
